/** Back office → Messages (C4): WhatsApp conversations, AI / human mode, 24 h window, hand-offs.
 * Design v2.1: a two-pane messenger (list + thread) in one glass window, iMessage-style bubbles,
 * a composer docked at the bottom of the thread; on a phone the list and the thread alternate. */

import {
  type Conversation,
  type ConversationModeEnum,
  useAssistantHandoffsList,
  useAssistantHandoffsResolveCreate,
  useInboxConversationsList,
  useInboxConversationsMessagesList,
  useInboxConversationsPartialUpdate,
  useInboxConversationsReadCreate,
  useInboxConversationsSendCreate,
  useInboxConversationsSimulateCreate,
  useInboxConversationsSummaryRetrieve,
} from "@agencyos/api-client";
import { formatPhone } from "@agencyos/core";
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  GlassCard,
  SegmentedControl,
  Skeleton,
  TextAreaField,
  TextField,
} from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, CircleAlert, Clock3, MessagesSquare, Search, Sparkles } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../components/AppShell";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { fieldErrors, useCurrentRole } from "../lib/settings";
import { Pager } from "./billing/shared";
import { ContactCalls } from "./calls/CallList";
import { CreateTicketFromConversation } from "./support/InboxTicket";

const POLL_MS = 5000; // no push yet: the inbox refreshes itself every few seconds
const ICON = { size: 16, strokeWidth: 1.75, "aria-hidden": true } as const;

function useRefreshInbox() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const key = String(query.queryKey[0] ?? "");
        return key.startsWith("/api/v1/inbox/") || key.startsWith("/api/v1/assistant/");
      },
    });
}

function phoneText(phone?: string | null): string {
  if (!phone) return "";
  try {
    return formatPhone(phone);
  } catch {
    return phone;
  }
}

function useWhen() {
  const { i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString(locale, { timeZone: "Africa/Douala", dateStyle: "short", timeStyle: "short" })
      : "";
}

/** Time only when it is today (Douala), the short date otherwise: like Messages on the Mac. */
function useShortWhen() {
  const { i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return (iso?: string | null) => {
    if (!iso) return "";
    const day = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: "Africa/Douala" });
    const date = new Date(iso);
    return day(date) === day(new Date())
      ? date.toLocaleTimeString(locale, { timeZone: "Africa/Douala", hour: "2-digit", minute: "2-digit" })
      : date.toLocaleDateString(locale, { timeZone: "Africa/Douala", day: "2-digit", month: "2-digit" });
  };
}

function Thread({ conversation, onBack }: { conversation: Conversation; onBack: () => void }) {
  const { t } = useTranslation();
  const when = useWhen();
  const refresh = useRefreshInbox();
  const messages = useInboxConversationsMessagesList(conversation.id, undefined, {
    query: { refetchInterval: POLL_MS },
  });
  const send = useInboxConversationsSendCreate({ mutation: { onSuccess: refresh } });
  const setMode = useInboxConversationsPartialUpdate({ mutation: { onSuccess: refresh } });
  const read = useInboxConversationsReadCreate({ mutation: { onSuccess: refresh } });
  const [body, setBody] = useState("");
  const errors = fieldErrors(send.error);
  const scroller = useRef<HTMLOListElement>(null);
  const name = conversation.contact_name || phoneText(conversation.contact_phone);

  useEffect(() => {
    if (conversation.unread_count) read.mutate({ id: conversation.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mark as read once per opening
  }, [conversation.id]);

  const items = messages.data?.status === 200 ? [...messages.data.data.results].reverse() : [];
  const last = items[items.length - 1]?.id;
  useEffect(() => {
    // Newest message in view, like any messenger (no smooth scroll: instant and motion-safe).
    const list = scroller.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [last]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!body.trim()) return;
    send.mutate({ id: conversation.id, data: { body } }, { onSuccess: () => setBody("") });
  };

  return (
    <section className="inbox__thread" aria-label={name}>
      <header className="inbox__thread-head">
        <button type="button" className="inbox__back ao-button ao-button--plain ao-button--sm" onClick={onBack}>
          <ChevronLeft {...ICON} />
          {t("inbox.back")}
        </button>
        <Avatar name={name} size="lg" />
        <div className="inbox__who">
          <h2>{name}</h2>
          <p className="muted">{phoneText(conversation.contact_phone)}</p>
        </div>
        <SegmentedControl<ConversationModeEnum>
          label={t("inbox.modeLabel")}
          value={conversation.mode ?? "ai"}
          options={[
            { value: "ai", label: t("inbox.mode.ai") },
            { value: "human", label: t("inbox.mode.human") },
          ]}
          onChange={(mode) => setMode.mutate({ id: conversation.id, data: { mode } })}
        />
        <CreateTicketFromConversation conversationId={conversation.id} />
      </header>
      <p role="note" className="inbox__note">
        {conversation.mode === "human" ? null : <Sparkles {...ICON} />}
        {conversation.mode === "human" ? t("inbox.humanMode") : t("inbox.takeOver")}
      </p>
      {conversation.contact ? <ContactCalls contactId={conversation.contact} compact limit={3} /> : null}
      <ol className="messages" aria-live="polite" ref={scroller}>
        {messages.isPending ? (
          <>
            <li className="bubble-skeleton">
              <Skeleton style={{ height: 44, width: "58%", borderRadius: 18 }} />
            </li>
            <li className="bubble-skeleton bubble-skeleton--out">
              <Skeleton style={{ height: 60, width: "64%", borderRadius: 18 }} />
            </li>
          </>
        ) : null}
        {items.map((message) => (
          <li key={message.id} className={`bubble bubble--${message.direction}${message.by_ai ? " bubble--ai" : ""}`}>
            {message.audio_url ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption -- its transcript is shown just below as text
              <audio controls preload="none" src={message.audio_url} aria-label={t("inbox.voice.note")} />
            ) : (
              <p className="bubble__body">{message.body || (message.transcript ? "" : `[${message.kind}]`)}</p>
            )}
            {message.transcript ? (
              <p className="bubble__body">
                <Badge>{t("inbox.transcript")}</Badge> {message.transcript}
              </p>
            ) : null}
            <p className="bubble__meta">
              {message.by_ai ? <span className="bubble__ai">{t("inbox.byAi")}</span> : message.sent_by_name ? `${message.sent_by_name} · ` : null}{" "}
              {when(message.created_at)}
              {message.direction === "out" ? ` · ${t(`inbox.status.${message.status}`)}` : ""}
            </p>
          </li>
        ))}
      </ol>
      <form onSubmit={onSubmit} className="inbox__composer">
        <p role="status" className={`inbox__window${conversation.window_open ? "" : " ko"}`}>
          {conversation.window_open ? <Clock3 {...ICON} /> : <CircleAlert {...ICON} />}
          {conversation.window_open
            ? t("inbox.windowOpen", { time: when(conversation.window_expires_at) })
            : t("inbox.windowClosed")}
        </p>
        <TextAreaField
          label={t("inbox.compose")}
          rows={2}
          value={body}
          disabled={!conversation.window_open}
          error={errors.body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="inbox__composer-actions">
          <VoiceRecorder conversationId={conversation.id} disabled={!conversation.window_open} onSent={refresh} />
          <Button type="submit" variant="whatsapp" disabled={!conversation.window_open || send.isPending}>
            {t("inbox.send")}
          </Button>
        </div>
      </form>
    </section>
  );
}

function Handoffs() {
  const { t } = useTranslation();
  const refresh = useRefreshInbox();
  const handoffs = useAssistantHandoffsList(undefined, { query: { refetchInterval: POLL_MS } });
  const resolve = useAssistantHandoffsResolveCreate({ mutation: { onSuccess: refresh } });
  const items = handoffs.data?.status === 200 ? handoffs.data.data.results : [];
  if (!items.length) return null;
  return (
    <GlassCard as="section" role="alert" className="stack notice-warning inbox__handoffs">
      <h2 className="ao-card__title">{t("inbox.handoffs")}</h2>
      <ul className="plain-list">
        {items.map((h) => (
          <li key={h.id} className="row between">
            <span>
              <strong>{h.contact_name}</strong> · <span className="muted">{t(`inbox.handoffReason.${h.reason}`)}</span>
            </span>
            <Button variant="tinted" size="sm" onClick={() => resolve.mutate({ id: h.id })}>
              {t("inbox.resolve")}
            </Button>
          </li>
        ))}
      </ul>
    </GlassCard>
  );
}

function Simulator() {
  const { t } = useTranslation();
  const refresh = useRefreshInbox();
  const simulate = useInboxConversationsSimulateCreate({ mutation: { onSuccess: refresh } });
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const errors = fieldErrors(simulate.error);
  return (
    <GlassCard as="section" className="stack">
      <div className="ao-card__header">
        <div>
          <h2 className="ao-card__title">{t("inbox.simulate")}</h2>
          <p className="ao-card__subtitle">{t("inbox.testMode")}</p>
        </div>
      </div>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          simulate.mutate({ data: { phone, name, body } }, { onSuccess: () => setBody("") });
        }}
      >
        <div className="grid-2">
          <TextField label={t("inbox.simulatePhone")} type="tel" value={phone} error={errors.phone} onChange={(e) => setPhone(e.target.value)} />
          <TextField label={t("inbox.simulateName")} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <TextAreaField label={t("inbox.simulateBody")} rows={2} value={body} error={errors.body} onChange={(e) => setBody(e.target.value)} />
        <div className="row">
          <Button type="submit" variant="secondary" disabled={simulate.isPending}>
            {t("inbox.simulateSend")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

export function InboxPage() {
  const { t } = useTranslation();
  const short = useShortWhen();
  const role = useCurrentRole();
  const [page, setPage] = useState(1);
  const conversations = useInboxConversationsList({ page }, { query: { refetchInterval: POLL_MS } });
  // D-115: the list is paginated; the unread figure covers every conversation.
  const summary = useInboxConversationsSummaryRetrieve({ query: { refetchInterval: POLL_MS } });
  const hasNext = conversations.data?.status === 200 && Boolean(conversations.data.data.next);
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const items = conversations.data?.status === 200 ? conversations.data.data.results : [];
  const opened = items.find((c) => c.id === openedId) ?? null;
  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? items.filter((c) => `${c.contact_name ?? ""} ${c.contact_phone ?? ""} ${c.last_message ?? ""}`.toLowerCase().includes(needle))
    : items;
  const unread = summary.data?.status === 200 ? summary.data.data.unread_messages : 0;

  return (
    <Page title={t("inbox.title")} description={unread ? t("inbox.unreadSummary", { count: unread }) : t("inbox.intro")}>
      <Handoffs />
      <div className={`inbox ao-glass${opened ? " inbox--open" : ""}`}>
        <section className="inbox__list" aria-label={t("inbox.conversations")}>
          <div className="inbox__search ao-input-icon">
            <Search size={15} strokeWidth={2} aria-hidden="true" />
            <input
              type="search"
              className="ao-field__input"
              aria-label={t("inbox.search")}
              placeholder={t("inbox.search")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          {conversations.isPending ? (
            <div className="inbox__skeleton" aria-hidden="true">
              {[0, 1, 2, 3].map((n) => (
                <div key={n} className="row">
                  <Skeleton className="ao-skeleton--circle" style={{ width: 36, height: 36 }} />
                  <div className="stack" style={{ flex: 1, gap: 6 }}>
                    <Skeleton style={{ height: 12, width: "50%" }} />
                    <Skeleton style={{ height: 10, width: "85%" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {!conversations.isPending && !items.length ? (
            <EmptyState compact icon={<MessagesSquare size={22} strokeWidth={1.75} />} title={t("inbox.empty")} />
          ) : null}
          <ul className="plain-list inbox__rows">
            {shown.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`conversation-row${c.id === openedId ? " conversation-row--open" : ""}${c.unread_count ? " conversation-row--unread" : ""}`}
                  aria-current={c.id === openedId ? "true" : undefined}
                  onClick={() => setOpenedId(c.id)}
                >
                  <Avatar name={c.contact_name || phoneText(c.contact_phone)} size="lg" />
                  <span className="conversation-row__text">
                    <span className="conversation-row__top">
                      <strong>{c.contact_name || phoneText(c.contact_phone)}</strong>
                      <time className="conversation-row__time" dateTime={c.last_message_at ?? undefined}>
                        {short(c.last_message_at)}
                      </time>
                    </span>
                    <span className="conversation-row__bottom">
                      <span className="muted conversation-row__preview">{c.last_message}</span>
                      {c.unread_count ? <span className="ao-count">{c.unread_count}</span> : null}
                    </span>
                    <span className={`conversation-row__mode conversation-row__mode--${c.mode ?? "ai"}`}>
                      {(c.mode ?? "ai") === "ai" ? <Sparkles size={12} strokeWidth={2} aria-hidden="true" /> : null}
                      {t(`inbox.mode.${c.mode ?? "ai"}`)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {page > 1 || hasNext ? (
            <Pager
              page={page}
              hasNext={hasNext}
              onChange={setPage}
              labels={{ previous: t("billing.previous"), next: t("billing.next"), page: t("billing.page", { page }) }}
            />
          ) : null}
        </section>
        {opened ? (
          <Thread key={opened.id} conversation={opened} onBack={() => setOpenedId(null)} />
        ) : (
          <div className="inbox__thread inbox__thread--empty">
            <EmptyState icon={<MessagesSquare size={22} strokeWidth={1.75} />} title={t("inbox.pick")} description={t("inbox.pickHint")} />
          </div>
        )}
      </div>
      {role === "owner" || role === "manager" ? <Simulator /> : null}
    </Page>
  );
}
